import { Stack } from "@mui/material";
import pluralize from "pluralize";

import { Rating } from "../Rating";
import { Text } from "../Text";

interface ReviewsSummaryProps {
  rating: number;
  ratingsCount: number;
  /**
   * Medium size is used in workplace details modal. It shows a h1 label with a rating and star indicator below
   * Small size is for inline use, it renders a row of rating and reviews count together
   */
  size?: "small" | "medium";
  /**
   * Controls the rating text format.
   * "detailed" shows "4.0 / 5 stars (1 rating)" (default)
   * "compact" shows "4.0 stars (1 rating)"
   */
  ratingFormat?: "compact" | "detailed";
}

function getRatingText(rating: number, ratingFormat: "compact" | "detailed"): string {
  return ratingFormat === "detailed"
    ? `${rating.toFixed(1)} / 5 stars`
    : `${rating.toFixed(1)} stars`;
}

function getRatingCountText(ratingsCount: number): string {
  return `(${ratingsCount} ${pluralize("rating", ratingsCount)})`;
}

export function ReviewsSummary(props: ReviewsSummaryProps) {
  const { rating, ratingsCount, size = "medium", ratingFormat = "detailed" } = props;

  return (
    <Stack
      spacing={3}
      alignItems={size === "medium" ? "flex-start" : "center"}
      direction={size === "medium" ? "column" : "row"}
    >
      <Stack direction="row" alignItems="center" spacing={4}>
        <Rating
          readOnly
          precision={0.5}
          value={rating}
          size={size === "medium" ? "medium" : "small"}
        />

        <Text bold variant={size === "medium" ? "body2" : "caption"}>
          {ratingsCount > 0
            ? `${getRatingText(rating, ratingFormat)} ${getRatingCountText(ratingsCount)}`
            : "Not yet rated"}
        </Text>
      </Stack>
    </Stack>
  );
}
